import {translation} from "./translation";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {HymnFinderComponent} from "./hymn-finder/hymn-finder.component";
import {MatSidenav, MatSidenavModule} from "@angular/material/sidenav";
import {NgIf} from "@angular/common";
import {LayoutService} from "./service/layout.service";
import {SidenavService} from "./service/sidenav.service";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {ThemeService} from "./service/theme.service";
import {MatMenuModule} from "@angular/material/menu";
import {MatSliderModule} from "@angular/material/slider";
import {MidiPlayerService} from "./service/midi-player.service";
import {HymnSelectionService} from "./service/hymn-selection.service";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {FormsModule} from "@angular/forms";
import {ContinuousPlaybackService} from "./service/continuous-playback.service";
import {AutoScrollService} from "./service/auto-scroll.service";
import {MatSnackBar, MatSnackBarModule} from "@angular/material/snack-bar";
import {SettingsManagerComponent} from "./settings-manager/settings-manager.component";
import {SettingsService} from "./service/settings.service";
import {BackButtonService} from "./service/back-button.service";
import {InfoMessageService} from "./service/info-message.service";
import {DebuggerComponent} from "./controls/debugger/debugger.component";
import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';
import { SplashScreen } from '@capacitor/splash-screen';
import { DownloadBannerComponent } from './download-banner/download-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HymnFinderComponent,
    MatSidenavModule,
    NgIf,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSliderModule,
    MatCheckboxModule,
    FormsModule,
    MatSnackBarModule,
    SettingsManagerComponent,
    DebuggerComponent,
    DownloadBannerComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = translation.title;

  @ViewChild('sidenavList') public sidenavList!: MatSidenav;
  @ViewChild('sidenavSettings') public sidenavSettings!: MatSidenav;

  safeAreaInsets: { top: number; right: number; bottom: number; left: number} = { top: 0, right: 0, bottom: 0, left: 0 };

  // In Angular, a standard @ViewChild is a snapshot, while a setter-based @ViewChild is a live listener. By using a setter (set sidenavContent(...)), you are telling Angular: "Whenever you notice this element appearing or disappearing in the DOM, call this function immediately."
  private _sidenavContent?: ElementRef;
  @ViewChild('main_content', { read: ElementRef })
  set sidenavContent(content: ElementRef) {
    if (content && !this._sidenavContent) {
      this._sidenavContent = content;
      if (this.resizeObserver) {
        this.resizeObserver.observe(content.nativeElement);
      }
    }
  }
  private resizeObserver!: ResizeObserver;

  constructor(public layoutService: LayoutService,
              private sidenavService: SidenavService,
              private router: Router,
              public themeService: ThemeService,
              public midiPlayerService: MidiPlayerService,
              public hymnSelectionService: HymnSelectionService,
              public continuousPlaybackService: ContinuousPlaybackService,
              private zone: NgZone,
              private snackbar: MatSnackBar,
              public autoScrollService: AutoScrollService,
              protected settingService: SettingsService,
              private backButtonService: BackButtonService, // do not remove in order to keep the back button functionality
              private changeDetectorRef: ChangeDetectorRef,
              private infoMessageService: InfoMessageService) {

    this.resizeObserver = new ResizeObserver(entries => {
      this.zone.run(() => {
        // Broadcast the new width whenever the sidenav-content resizes
        for (let entry of entries) {
          this.layoutService.contentWidth$.next(entry.contentRect.width);
        }
      });
    });

    sidenavService.sidenavState$.subscribe((isOpen: boolean) => {

      window.addEventListener('unhandledrejection', event => {
        console.error('🔥 UNHANDLED PROMISE REJECTION:', event.reason);
      });

      this.disableTextZoom();
      if(this.sidenavList) {
        if (isOpen) {
          this.zone.run(() => {
            this.sidenavList.open();
          });
        } else {
          this.zone.run(() => {
            this.sidenavList.close();
          });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.router.events
      .subscribe(() => {
        if(document.querySelector('.mat-sidenav-content')) {
          document.querySelector('.mat-sidenav-content')!.scrollTop = 0;
        }
      });

    this.infoMessageService.checkForInfoMessage();

    if (this.sidenavContent) {
      this.resizeObserver.observe(this.sidenavContent.nativeElement);
    }

    // Coordinate splash removal once everything is ready
    this.markAppReadyOnce();
  }

  async ngOnInit() {
    this.layoutService.initSafeArea();

    this.settingService.settingsState$.subscribe((isOpen: boolean) => {
      if(this.sidenavSettings) {
        if (isOpen) {
          this.sidenavSettings.open();
        } else {
          this.sidenavSettings.close();
        }
      }
    });
  }

  private appReadyTriggered = false;
  private async markAppReadyOnce() {
    if (this.appReadyTriggered) return;
    this.appReadyTriggered = true;

    const platform = Capacitor.getPlatform?.() ?? 'unknown';
    const splashAvailable = (Capacitor as any).isPluginAvailable ? Capacitor.isPluginAvailable('SplashScreen') : true;
    try {
      // Wait for theme readiness, but don't block forever (race with 4s cap)
      await Promise.race([
        this.waitUntil(() => !this.themeService.loading, 15000),
        this.delay(4000)
      ]);

      // Ensure sidenav reflects desired state (do not await animations)
      try {
        if (this.sidenavList) {
          if (this.sidenavService.hymnListOpen) {
            this.sidenavList.open();
          } else {
            this.sidenavList.close();
          }
        }
      } catch {}

      await this.whenZoneStable();
      await this.nextFrame();
      await this.nextFrame();

      // Hide native splash (Capacitor)
      if (Capacitor.isNativePlatform && Capacitor.isNativePlatform() && splashAvailable) {
        try {
          await SplashScreen.hide();
          await this.delay(50);
        } catch (err) {
          console.warn('[Splash] hide failed on', platform, err);
        }
      }

      // Fade out and remove web loader overlay if present
      const loader = document.getElementById('app-loader');
      if (loader) {
        loader.classList.add('hidden');
        setTimeout(() => loader.classList.add('removed'), 250);
      }
    } catch (e) {
      console.warn('App ready timeout or error:', e);
      // Best-effort hide in case of timeout
      if (Capacitor.isNativePlatform && Capacitor.isNativePlatform() && splashAvailable) {
        try { await SplashScreen.hide(); } catch {}
      }
      const loader = document.getElementById('app-loader');
      if (loader) loader.classList.add('removed');
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(res => setTimeout(res, ms));
  }

  private async whenZoneStable(): Promise<void> {
    return new Promise(resolve => {
      if ((this as any).ngZone?.isStable) {
        resolve();
      } else {
        const sub = this.zone.onStable.subscribe(() => {
          sub.unsubscribe();
          resolve();
        });
      }
    });
  }

  private async nextFrame(): Promise<void> {
    return new Promise(res => requestAnimationFrame(() => res()));
  }

  private async waitUntil(predicate: () => boolean, timeoutMs = 10000, checkEveryMs = 50): Promise<void> {
    const start = Date.now();
    return new Promise((resolve, reject) => {
      const tick = () => {
        if (predicate()) return resolve();
        if (Date.now() - start >= timeoutMs) return reject(new Error('waitUntil timeout'));
        setTimeout(tick, checkEveryMs);
      };
      tick();
    });
  }

  // Disable text zoom on mobile platforms
  async disableTextZoom() {
    if (Capacitor.isNativePlatform()) {
      // The plugin uses a decimal value, where 1.0 is 100%
      await TextZoom.set({ value: 1.0 });
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.autoScrollService.stopAutoScroll();
  }
}
